import { useTranslation } from 'react-i18next';

import type { Schemas } from '../api/client.js';
import { formatDate, intlLocale } from '../i18n.js';
import { LogoMark } from './Layouts.js';
import { formatMoney } from './site.js';

type Status = Schemas['Invoice']['status'];

const TONE: Record<Status, string> = {
  open: 'warn',
  paid: 'ok',
  void: 'neutral',
  refunded: 'neutral',
};

/** Open after its due date: overdue (audit, 10.6). The API keeps such an invoice "open". */
export function isOverdue(invoice: { status: Status; dueAt: string | null }, now = Date.now()) {
  return invoice.status === 'open' && invoice.dueAt !== null && Date.parse(invoice.dueAt) < now;
}

/**
 * The state of an invoice as a badge of the sheets (billing.html, invoice.html). `overdue` from the
 * API where it says so (the operator's views), else by the browser's clock.
 */
export function InvoiceBadge({
  invoice,
  overdue: told,
}: {
  invoice: { status: Status; dueAt: string | null };
  overdue?: boolean;
}) {
  const { t } = useTranslation();
  const overdue = told ?? isOverdue(invoice);
  return (
    <span className={`badge ${overdue ? 'bad' : TONE[invoice.status]} nodot`}>
      {overdue ? t('invoice.overdue') : t(`invoice.status.${invoice.status}`)}
    </span>
  );
}

export function vatLabel(rateBp: number) {
  return `${(rateBp / 100).toLocaleString('de-DE')} %`;
}

/**
 * The invoice as a document (sheet: invoice.html .invdoc): prints on its own (T-48); a demo invoice
 * says so with a stamp. Terms of payment stand only on an open invoice (audit, 10.6).
 */
export function InvoiceDocument({
  invoice,
  timeZone,
}: {
  invoice: Schemas['InvoiceDocument'];
  timeZone?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const date = (iso: string) => formatDate(iso, lang, timeZone);
  const money = (cents: number) => formatMoney(cents, lang);
  const b = invoice.buyer;
  const country = (code: string) =>
    new Intl.DisplayNames([intlLocale(lang)], { type: 'region' }).of(code) ?? code;
  const brand = t('app.name');
  // Without a configured seller the API names the brand alone, which the logo already shows.
  const from = invoice.issuer.filter((line) => line !== brand);
  const open = invoice.status === 'open';
  const days = Math.round((Date.parse(invoice.dueAt) - Date.parse(invoice.issuedAt)) / 86_400_000);
  return (
    <article className="invdoc">
      {invoice.demo ? <div className="stamp">{t('invoice.demoStamp')}</div> : null}
      <div className="head">
        <div>
          <span className="logo">
            <LogoMark />
            {brand}
          </span>
          {from.length > 0 ? <p className="from">{from.join(' · ')}</p> : null}
        </div>
        <div>
          <h2>{t('invoice.title')}</h2>
          <div className="no">{invoice.number}</div>
          {/* Only on paper: the state beside the paper is not printed (account.css). */}
          {invoice.status === 'void' || invoice.status === 'refunded' ? (
            <div className="state">{t(`invoice.status.${invoice.status}`)}</div>
          ) : null}
        </div>
      </div>

      <div className="parties">
        <div>
          <span className="lab">{t('invoice.to')}</span>
          <span>{b.name}</span>
          {b.street ? <span>{b.street}</span> : null}
          {b.postalCode || b.city ? <span>{`${b.postalCode} ${b.city}`.trim()}</span> : null}
          <span>{country(b.country)}</span>
          {b.vatId ? <span>{`${t('invoice.vatId')}: ${b.vatId}`}</span> : null}
          {b.vatProof ? (
            <span className="proof">
              {t('invoice.vatProof', { date: date(b.vatProof.checkedAt) })}
            </span>
          ) : null}
        </div>
        <dl>
          <dt>{t('invoice.issued')}</dt>
          <dd>{date(invoice.issuedAt)}</dd>
          <dt>{t('invoice.due')}</dt>
          <dd>{date(invoice.dueAt)}</dd>
          {invoice.paidAt ? (
            <>
              <dt>{t('invoice.paidOn')}</dt>
              <dd>{date(invoice.paidAt)}</dd>
            </>
          ) : null}
        </dl>
      </div>

      <table>
        <thead>
          <tr>
            <th scope="col">{t('invoice.item')}</th>
            <th scope="col" className="num">
              {t('invoice.amount')}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              {invoice.description}
              {invoice.periodStart && invoice.periodEnd ? (
                <span>
                  {t('invoice.period', {
                    from: date(invoice.periodStart),
                    to: date(invoice.periodEnd),
                  })}
                </span>
              ) : invoice.interval ? (
                <span>
                  {t('invoice.termLength', {
                    count: invoice.interval === 'year' ? 12 : 1,
                  })}
                </span>
              ) : null}
            </td>
            <td className="num">{money(invoice.netCents)}</td>
          </tr>
          <tr>
            <th scope="row">{t('invoice.net')}</th>
            <td className="num">{money(invoice.netCents)}</td>
          </tr>
          <tr>
            <th scope="row">{t('invoice.vat', { rate: vatLabel(invoice.vatRateBp) })}</th>
            <td className="num">{money(invoice.vatCents)}</td>
          </tr>
          <tr className="total">
            <th scope="row">{t('invoice.gross')}</th>
            <td className="num">{money(invoice.grossCents)}</td>
          </tr>
        </tbody>
      </table>

      {invoice.vatNote ? <p className="note">{t(`invoice.vatNote.${invoice.vatNote}`)}</p> : null}

      {open ? (
        <div className="pay">
          <b>{t('invoice.howToPay')}</b>
          <p>
            {t('invoice.payText', {
              amount: money(invoice.grossCents),
              date: date(invoice.dueAt),
              number: invoice.number,
            })}
          </p>
          {invoice.bank.length > 0 ? (
            <p className="bank">
              {invoice.bank.map((line, i) => (
                <span key={i}>{line}</span>
              ))}
            </p>
          ) : (
            <p>
              <b>{t('invoice.noBank')}</b>
            </p>
          )}
        </div>
      ) : null}
      {open ? (
        <p className="foot">
          {[
            days > 0 ? t('invoice.footPay', { count: days }) : t('invoice.footPayNow'),
            // A plan bought on invoice starts with the payment; a service has no such period.
            invoice.planCode && !invoice.periodStart ? t('invoice.footPeriod') : null,
            t('invoice.footTerms'),
          ]
            .filter(Boolean)
            .join(' ')}
        </p>
      ) : null}
    </article>
  );
}
